/* eslint-disable indent */
// This is a simplified builder to manage the Assay Creation config.
import differenceInSeconds from 'date-fns/differenceInSeconds';
import parseISO from 'date-fns/parseISO';

import { Aggregation, Metric } from '../../openapi';
import {
  RawBaselineDefinition,
  RawRunSummary,
  RawSummarizerMetadata,
  RawWindowDefinition as Window
} from '../../types/assay.types';

import { startDateTimeMinusWidth } from './helper';

type SettingsType = {
  threshold: string,
  aggType: Aggregation,
  numBins: number,
  binMode: string,
  binWeights: number[],
  metric: Metric,
  edges: number[] | null,
  next_run_time: number,
  run_freq_num: number,
  run_freq: number,
  grouping_freq: string
}

export enum MonitorType {
  // eslint-disable-next-line no-unused-vars
  OUTPUT, INPUT,
}

// Overwrite the config values if specified, or ignore them if omitted (do not use `undefined` directly)
interface ApplyMonitorArgs {
  monitored_type: MonitorType | null | undefined

  monitored_field: string | null | undefined

  monitored_column: number | null | undefined
}

const DEFAULT_FIXED_WINDOW = {
  'pipeline_name': null,
  'model_name': null,
  'workspace_id': null,
  'start': null,
  'end': null,
  'locations': null,
  'width': '24 hours',
  'path': null, // Not used within data structure - use accessor function instead
};

export class AssayConfig {
  name: string | null = '';

  pipeline_id = 0;

  pipeline_name: string | null = null;

  workspace_name: string | null = null;

  active = true;

  status = 'created';

  get iopath() {
    return this.getIOPath()
  }

  // iopath is really 3 separate properties
  monitored_type = MonitorType.OUTPUT

  monitored_field: string | null = null

  monitored_column: string | null = null

  baseline: RawBaselineDefinition = {
    calculated: {
      'fixed_window': DEFAULT_FIXED_WINDOW,
    }
  };

  window: Window = {
    'pipeline_name': null,
    'model_name': null,
    'workspace_id': null,
    'width': '24 hours',
    'path': null, // Not used within data structure - use accessor function instead
    'start': null,
    'end': null,
    'interval': null,
    'locations': null,
  };

  summarizer: RawSummarizerMetadata = {
    'type': 'UnivariateContinuous',
    'bin_mode': 'Quantile',
    'aggregation': 'Density',
    'metric': 'PSI',
    'num_bins': 5,
    'bin_weights': null,
    'bin_width': null,
    'provided_edges': null,
    'alert_threshold': null,
    'add_outlier_edges': true
  };

  warning_threshold: number | null = null;

  alert_threshold = 0.1;

  run_until = null;

  lastRun: string | null = null;

  workspace_id = 0;

  constructor(oldAssayConfig = {}, overrides = {}) {
    Object.assign(this, oldAssayConfig);
    Object.assign(this, overrides);
  }

  getModel() {
    return this.window.model_name;
  }

  getMonitoredType() {
    return this.monitored_type;
  }

  getMonitoredField() {
    return this.monitored_field;
  }

  getMonitoredColumn() {
    return this.monitored_column;
  }

  setModel(model_name: string) {
    this.window.model_name = model_name;
    if (this.baseline?.calculated?.fixed_window) {
      this.baseline.calculated.fixed_window.model_name = model_name;
    }
    return this;
  }

  setPipeline(pipeline_id: number, pipeline_name: string) {
    this.pipeline_id = pipeline_id;
    this.pipeline_name = pipeline_name;
    this.window.pipeline_name = pipeline_name;

    if (this.baseline?.calculated?.fixed_window) {
      this.baseline.calculated.fixed_window.pipeline_name = pipeline_name;
    }
    return this;
  }

  setBaselineRange(start: string, end: string) {
    if (!this.baseline?.calculated?.fixed_window) {
      this.baseline = { calculated: { fixed_window: DEFAULT_FIXED_WINDOW } };
    }
    if (this.baseline?.calculated?.fixed_window) {
      this.baseline.calculated.fixed_window.start = start;
      this.baseline.calculated.fixed_window.end = end;

      const startDate = parseISO(start);
      const endDate = parseISO(end);
      const seconds = differenceInSeconds(endDate, startDate);

      if (seconds === 0) {
        this.baseline.calculated.fixed_window.width = '1 day';
      } else {
        this.baseline.calculated.fixed_window.width = `${seconds} seconds`;
      }
    }

    return this;
  }

  setBaselineVector(vector: (number | null)[]) {
    this.baseline = { vector };
    return this;
  }

  setBaselineStaticSummary(baselineSummary: RawRunSummary) {
    // We conserve the other parts of the config until creation, and then we send only static
    this.baseline = { ...this.baseline, static: baselineSummary };
    return this;
  }

  setSummarizerOptions(binNum: number, binWeights: number[], alertThreshold: number, metric: Metric) {
    this.summarizer.metric = metric;
    this.summarizer.num_bins = binNum;
    this.summarizer.bin_weights = binWeights;
    this.summarizer.alert_threshold = alertThreshold;
    return this;
  }

  // NOTE: In the future, this will likely require an input/output enum and 2 ints.
  // setMonitor(str: string) {
  //   this.iopath = str;
  //   return this;
  // }

  applyMonitor(args: ApplyMonitorArgs) {
    return Object.assign(this, args);
  }

  getIOPath() {
    if (!this.monitored_field || !this.monitored_column) {
      // Intentional warning
      // eslint-disable-next-line no-console
      console.warn('IO path was requested without field or column specified!', this.monitored_field, this.monitored_column);
      return '';
    }

    const INFERENCE_DATA_KEYS_REGEX = /^((in)|(out))\.(?<name>.*)/;
    const type = this.monitored_type === MonitorType.OUTPUT ? 'output' : 'input';

    const field = this.monitored_field.match(INFERENCE_DATA_KEYS_REGEX)?.groups?.name;
    const column = this.monitored_column === 'None' ? null : this.monitored_column;
    if (!field) {
      // This may be legacy JSON format.
      if (!column) {
        return `${type} ${this.monitored_field}`;
      }
      return `${type} ${this.monitored_field} ${column}`;
    }

    if (!column) {
      return `${type} ${field}`;
    }
    return `${type} ${field} ${column}`;
  }

  setName(name: string) {
    this.name = name;
    return this;
  }

  setWorkspace(workspace_id: number, workspace_name: string | null = null) {
    this.workspace_id = workspace_id;
    this.workspace_name = workspace_name;
    this.window.workspace_id = workspace_id;

    if (this.baseline?.calculated?.fixed_window) {
      this.baseline.calculated.fixed_window.workspace_id = workspace_id;
    }
    return this;
  }


  setLocations(locations: null | any[]) {
    this.window.locations = locations?.length ? locations.map(({ name }) => name) : null;

    return this;
  }

  setAdvancedSettings(settings: SettingsType, start: number) {
    this.alert_threshold = parseFloat(settings.threshold);
    this.summarizer.aggregation = settings.aggType
    this.summarizer.num_bins = settings.numBins;
    this.summarizer.bin_mode = settings.binMode;
    this.summarizer.bin_weights = settings.binWeights;
    this.summarizer.metric = settings.metric;
    this.summarizer.provided_edges = settings.edges;

    // This is a hack from https://wallarooai.atlassian.net/browse/ENG-2740
    // V1 sets the start time back based on the window width, to deal with the weird start scheduling behavior.
    // V2 will use the preview start date, but it won't start until after the start date (it uses the start date to ensure runs are evenly spaced.)
    this.window.start = startDateTimeMinusWidth(new Date(start), settings.grouping_freq);
    this.window.interval = `${settings.run_freq_num} ${settings.run_freq}`;
    this.window.width = `1 ${settings.grouping_freq}`

    return this;
  }
}
